/**
 * @file index.tsx
 *
 * @fileoverview Renders text as a particular style (custom styles).
 */
import { cva, VariantProps } from "class-variance-authority";
import React from "react";
import {
  excludeSpacingProps,
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../../theme/spacing";
import { getScopedClasses } from "../../../../utils/responsive";
import styles from "./text.module.scss";

const text = cva([styles.text], {
  variants: {
    color: {
      white: styles.white,
      primary: styles.primary,
      primaryLight: styles.primaryLight,
      primaryVeryLight: styles.primaryVeryLight,
      secondary: styles.secondary,
      secondaryLight: styles.secondaryLight,
      secondaryVeryLight: styles.secondaryVeryLight,
      default: styles.default,
      success: styles.success,
      successLight: styles.successLight,
      successVeryLight: styles.successVeryLight,
      danger: styles.danger,
      dangerLight: styles.dangerLight,
      dangerVeryLight: styles.dangerVeryLight,
      warning: styles.warning,
      warningLight: styles.warningLight,
      warningVeryLight: styles.warningVeryLight,
      gray0: styles.gray0,
      gray1: styles.gray1,
      gray2: styles.gray2,
      gray3: styles.gray3,
      gray4: styles.gray4,
      gray5: styles.gray5,
      gray6: styles.gray6,
      gray7: styles.gray7,
      gray8: styles.gray8,
      gray9: styles.gray9,
      black: styles.black,
    },
  },
});

type FontWeightProps =
  | "normal"
  | "semibold"
  | "bold"
  | 400
  | 500
  | 700
  | 600
  | 900;

type FontStyleProps = "italic" | "normal" | "oblique";

type TextAlignProps =
  | "center"
  | "end"
  | "justify"
  | "left"
  | "match-parent"
  | "right"
  | "start";

type BaseTextProps = VariantProps<typeof text> & SpaceProps;

export interface TextProps extends BaseTextProps {
  /**
   * The text to display.
   */
  children: React.ReactNode;
  /**
   * The tag to use.
   *
   * @default "p"
   */
  tag?: any;
  /**
   * Text decoration property.
   *
   * @default "none"
   */
  textDecoration?: string;
  /**
   * Text decoration property.
   *
   * @default "none"
   */
  textTransform?: string;
  /**
   * Responsive font size prop.
   */
  fontSize?: number | number[];
  /**
   * Responsive line height prop.
   */
  lineHeight?: number | number[];
  /**
   * Responsive font weight props.
   */
  fontWeight?: FontWeightProps | FontWeightProps[];
  /**
   * Responsive font style props.
   */
  fontStyle?: FontStyleProps | FontStyleProps[];
  /**
   * Responsive text align props.
   */
  textAlign?: TextAlignProps | TextAlignProps[];
  /**
   * className for externally overriding atom.
   */
  className?: string;
}

export const Text = ({
  children,
  tag = "p",
  color,
  textDecoration,
  textTransform,
  fontSize,
  lineHeight,
  fontWeight,
  fontStyle,
  textAlign,
  className = "",
  ...props
}: TextProps) => {
  const Tag = tag;

  return (
    <Tag
      className={`
      ${className}
      ${text({ color })}
      ${generateMarginClasses(props)}
      ${generatePaddingClasses(props)}
      ${getScopedClasses(styles, "fs", fontSize)}
      ${getScopedClasses(styles, "lh", lineHeight)}
      ${getScopedClasses(styles, "fw", fontWeight)}
      ${getScopedClasses(styles, "fst", fontStyle)}
      ${getScopedClasses(styles, "ta", textAlign)}
      ${getScopedClasses(styles, "td", textDecoration)}
      ${getScopedClasses(styles, "tt", textTransform)}
      `.trim()}
      {...excludeSpacingProps(props)}
    >
      {children}
    </Tag>
  );
};
export default Text;
